/**
 * How an alert rule's event types are SHOWN and PICKED — with the exact string the AI module
 * sends next to every friendly name.
 *
 * A rule stores raw strings ("panel_reading", "no_belt_movement") and matches an event when
 * either its `event_type` or its `event_subtype` equals one of them. The friendly names
 * ("Panel Reading — all events") alone left an operator guessing which field and which
 * spelling a choice meant, so every place a type appears now carries both:
 *
 *   Panel Reading — all events
 *   event_type = panel_reading
 *
 * Which field a string is comes from the command-center's event catalog (what the AI module
 * has actually sent); built-in options carry their own description.
 *
 * Same components in the local and the cloud Alert Rules page.
 */
import { useState } from 'react';
import type { EventCatalogEntry } from './ruleConditionsModel';
import { catalogEventOptions } from './ruleConditionsModel';

export interface EventTypeOption {
  value: string;
  label: string;
}

/** What a stored token is: which event field it matches, and what to call it. */
export interface EventTypeDescription {
  token: string;
  name: string;
  /** Which field of the AI event this string is compared with. */
  field: 'all' | 'event_type' | 'event_subtype' | 'model_key' | 'type_or_subtype';
  /** Seen from the AI module (event catalog) rather than a built-in option. */
  fromAi: boolean;
}

const FIELD_TEXT: Record<EventTypeDescription['field'], string> = {
  all: 'any event_type',
  event_type: 'event_type',
  event_subtype: 'event_subtype',
  model_key: 'model key',
  type_or_subtype: 'event_type / event_subtype',
};

export const parseEventTypeTokens = (value?: string | null): string[] =>
  String(value ?? '').split(',').map((t) => t.trim()).filter(Boolean);

/** Describe one stored token using the catalog first, then the built-in options. */
export function describeEventType(
  token: string,
  builtin: EventTypeOption[],
  catalog: EventCatalogEntry[],
): EventTypeDescription {
  if (token === '*') return { token, name: 'All event types', field: 'all', fromAi: false };
  const asType = (catalog || []).some((e) => e.event_type === token);
  const asSub = (catalog || []).some((e) => e.event_subtype === token);
  if (asType || asSub) {
    const name = catalogEventOptions(catalog).find((o) => o.value === token)?.label || token;
    return { token, name, field: asType ? 'event_type' : 'event_subtype', fromAi: true };
  }
  const b = builtin.find((o) => o.value === token);
  if (b) {
    const modelKey = /\(model key\)\s*$/i.test(b.label);
    return {
      token,
      name: b.label.replace(/\s*\(model key\)\s*$/i, ''),
      field: modelKey ? 'model_key' : 'type_or_subtype',
      fromAi: false,
    };
  }
  return { token, name: token, field: 'type_or_subtype', fromAi: false };
}

/** "event_type = panel_reading" — the exact string, and the field it is matched against. */
export function rawText(d: EventTypeDescription): string {
  return d.field === 'all' ? '*' : `${FIELD_TEXT[d.field]} = ${d.token}`;
}

/** One event type as a chip: friendly name, and the exact string under it. */
export function EventTypeChip({
  desc, onRemove, compact,
}: {
  desc: EventTypeDescription;
  onRemove?: () => void;
  compact?: boolean;
}) {
  return (
    <span
      className={`inline-flex max-w-full items-start gap-1 rounded-md border px-1.5 py-0.5 ${
        desc.fromAi ? 'border-teal-500/30 bg-teal-500/10' : 'border-brand/25 bg-brand/10'
      }`}
      title={`${desc.name}\n${rawText(desc)}`}
    >
      <span className="min-w-0 leading-tight">
        <span className={`block truncate font-medium ${compact ? 'text-[11px]' : 'text-xs'} ${desc.fromAi ? 'text-teal-600 dark:text-teal-300' : 'text-brand'}`}>
          {desc.name}
        </span>
        <code className="block truncate font-mono text-[10px] text-muted-foreground">{rawText(desc)}</code>
      </span>
      {onRemove && (
        <button type="button" onClick={onRemove} className="ml-0.5 leading-none text-muted-foreground hover:text-red-400" title="Remove" aria-label={`Remove ${desc.name}`}>
          ×
        </button>
      )}
    </span>
  );
}

/** The rules table's event-types cell: up to `max` chips, the rest as a count with the full list on hover. */
export function EventTypesSummary({
  value, builtin, catalog, max = 2,
}: {
  value?: string | null;
  builtin: EventTypeOption[];
  catalog: EventCatalogEntry[];
  max?: number;
}) {
  const tokens = parseEventTypeTokens(value);
  if (tokens.length === 0 || tokens.includes('*')) {
    return (
      <span className="leading-tight">
        <span className="block text-[12px] text-muted-foreground">All event types</span>
        <code className="block font-mono text-[10px] text-muted-foreground">*</code>
      </span>
    );
  }
  const descs = tokens.map((t) => describeEventType(t, builtin, catalog));
  const rest = descs.length - max;
  return (
    <div className="flex flex-wrap items-start gap-1" title={descs.map((d) => `${d.name} — ${rawText(d)}`).join('\n')}>
      {descs.slice(0, max).map((d) => <EventTypeChip key={d.token} desc={d} compact />)}
      {rest > 0 && <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">+{rest}</span>}
    </div>
  );
}

function timeAgo(iso?: string): string {
  if (!iso) return '—';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (!Number.isFinite(s)) return '—';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

/**
 * The event-type picker: chips for what is selected, a list grouped into "received from AI
 * analytics" and "built-in", free typing for anything else, and — for the selection — the
 * exact event_type / event_subtype pairs the AI module has sent that this rule will match.
 */
export function EventTypePicker({
  value, onChange, builtin, catalog, inputCls,
}: {
  value?: string | null;
  onChange: (v: string) => void;
  builtin: EventTypeOption[];
  catalog: EventCatalogEntry[];
  inputCls: string;
}) {
  const [custom, setCustom] = useState('');
  const selected = parseEventTypeTokens(value);

  const commit = (next: string[]) => {
    const uniq = [...new Set(next.map((t) => String(t).trim()).filter(Boolean))];
    // '*' (all) is exclusive: it can't coexist with specific types.
    const cleaned = uniq.includes('*') && uniq.length > 1 ? uniq.filter((t) => t !== '*') : uniq;
    onChange(cleaned.join(','));
  };
  const add = (v: string) => {
    const t = String(v || '').trim();
    if (!t) return;
    if (t === '*') return commit(['*']);
    commit([...selected.filter((x) => x !== '*'), t]);
  };
  const remove = (v: string) => commit(selected.filter((t) => t !== v));

  const aiOptions = catalogEventOptions(catalog).filter((o) => !selected.includes(o.value));
  const aiValues = new Set(catalogEventOptions(catalog).map((o) => o.value));
  const builtinOptions = builtin.filter((o) => !selected.includes(o.value) && !aiValues.has(o.value));
  const optionText = (o: EventTypeOption) => {
    const d = describeEventType(o.value, builtin, catalog);
    return `${d.name}   ·   ${rawText(d)}`;
  };

  // The catalog rows this selection matches: the exact pairs the AI module has sent.
  const all = selected.length === 0 || selected.includes('*');
  const matched = (catalog || []).filter(
    (e) => all || selected.includes(e.event_type) || selected.includes(e.event_subtype),
  );

  return (
    <div>
      <div className="mt-0.5 flex min-h-[38px] flex-wrap items-center gap-1 rounded-lg border border-border bg-background px-2 py-1.5">
        {selected.length === 0 && <span className="text-sm text-muted-foreground">All event types (*)</span>}
        {selected.map((t) => (
          <EventTypeChip key={t} desc={describeEventType(t, builtin, catalog)} onRemove={() => remove(t)} />
        ))}
      </div>
      <div className="mt-1 flex gap-1">
        <select value="" onChange={(e) => add(e.target.value)} className={inputCls} aria-label="Add event type">
          <option value="" disabled>Add event type…</option>
          {aiOptions.length > 0 && (
            <optgroup label="Received from AI analytics">
              {aiOptions.map((o) => <option key={o.value} value={o.value}>{optionText(o)}</option>)}
            </optgroup>
          )}
          <optgroup label="Built-in detections">
            {builtinOptions.map((o) => <option key={o.value} value={o.value}>{optionText(o)}</option>)}
          </optgroup>
        </select>
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(custom); setCustom(''); } }}
          className={`${inputCls} font-mono`}
          placeholder="or type exact string…"
        />
        <button type="button" onClick={() => { add(custom); setCustom(''); }} className="shrink-0 rounded-lg border border-border px-3 text-sm text-muted-foreground hover:bg-muted">
          Add
        </button>
      </div>

      {catalog.length > 0 && (
        <div className="mt-2 overflow-hidden rounded-lg border border-border">
          <div className="bg-muted/50 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
            {all ? 'Every event received from AI analytics' : 'Events received from AI analytics that this rule matches'}
          </div>
          {matched.length === 0 ? (
            <div className="px-2.5 py-1.5 text-[11px] text-muted-foreground">
              None received yet for this selection. Built-in detections are matched the usual way.
            </div>
          ) : (
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="px-2.5 py-1 font-medium">event_type</th>
                  <th className="px-2.5 py-1 font-medium">event_subtype</th>
                  <th className="px-2.5 py-1 text-right font-medium">events</th>
                  <th className="px-2.5 py-1 text-right font-medium">last received</th>
                </tr>
              </thead>
              <tbody>
                {matched.map((e) => (
                  <tr key={`${e.event_type}|${e.event_subtype}`} className="border-t border-border">
                    <td className="px-2.5 py-1 font-mono text-foreground">{e.event_type}</td>
                    <td className="px-2.5 py-1 font-mono text-foreground">{e.event_subtype || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-2.5 py-1 text-right tabular-nums text-muted-foreground">{Number(e.event_count || 0).toLocaleString()}</td>
                    <td className="px-2.5 py-1 text-right text-muted-foreground">{timeAgo(e.last_seen_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
